import { PlotReport } from '@/api/rest/resources/types/dashboard';
import {
  IndicatorFactType,
  LABEL_FILTERS,
  PlotReportFactElement,
  PlotReportFactLabel,
  PlotReportFactLabelFilter,
} from '@/api/rest/resources/types/fact';
import { OrganizationReportingFacts } from '@/api/rest/resources/types/organization';
import { ProjectReport } from '@/api/rest/resources/types/project';
import { isNullish } from '@/utils/booleans';

export const formatFactLabelsForRequest = (factLabels?: PlotReportFactLabelFilter) => {
  if (isNullish(factLabels)) return {};
  return Object.fromEntries(Object.entries(factLabels).map(([key, value]) => [`label__${key}`, value]) ?? []);
};

export const hasLabel = (filterLabel: PlotReportFactLabelFilter, factLabels: PlotReportFactLabel[]) => {
  if (Object.entries(filterLabel).length !== factLabels.length) {
    return false;
  }
  return Object.entries(filterLabel).every(
    ([key, value]) =>
      factLabels.some((label) => label.name === key && label.value === value) || value === LABEL_FILTERS.ALL,
  );
};

export const getFact = <T = unknown>(
  plotReport: ProjectReport | PlotReport | OrganizationReportingFacts,
  factType: IndicatorFactType,
  factLabelFilters?: PlotReportFactLabelFilter,
  isFactHidden?: boolean,
) => {
  if (isFactHidden) return undefined;

  return getFactFromFactArray<T>(plotReport.facts, factType, factLabelFilters);
};

export const getFacts = <T = unknown>(
  plotReport: ProjectReport | PlotReport | OrganizationReportingFacts,
  factType: IndicatorFactType[],
  factLabelFilters?: PlotReportFactLabelFilter,
  isFactHidden?: boolean,
) => {
  if (isFactHidden) return undefined;

  return plotReport.facts.filter(
    (f) => factType.includes(f.name) && (factLabelFilters && f.labels ? hasLabel(factLabelFilters, f.labels) : true),
  ) as PlotReportFactElement<T | null>[] | undefined;
};

export const getFactFromFactArray = <T = unknown>(
  facts: PlotReportFactElement<unknown>[] | undefined,
  factType: IndicatorFactType,
  labels?: PlotReportFactLabelFilter,
) => {
  if (!facts) return undefined;

  return facts.find((f) => f.name === factType && (labels && f.labels ? hasLabel(labels, f.labels) : true)) as
    | PlotReportFactElement<T | null>
    | undefined;
};

export const getFactsFromFactArray = <T = unknown>(
  facts: PlotReportFactElement<unknown>[] | undefined,
  factType: IndicatorFactType,
  labels?: PlotReportFactLabelFilter,
) => {
  if (!facts) return undefined;

  return facts.filter((f) => f.name === factType && (labels && f.labels ? hasLabel(labels, f.labels) : true)) as
    | PlotReportFactElement<T | null>[]
    | undefined;
};
